'use client'

import { FormSubmit as AriaKitFormSubmit } from '@ariakit/react'
import { ComponentProps } from 'react'
import styled from 'styled-components'

import { Button } from '@syconium/little-miss-figgy/dist/components/Button/Button'

import { FormSubmitContent } from './FormSubmitContent'

export type FormSubmitProps = {
  children: React.ReactNode
  submittingLabel: string
  isSubmitting?: boolean
} & Omit<ComponentProps<typeof Button>, 'type' | 'children'>

/**
 * FormSubmit component that integrates Button with form submission state.
 * Shows loading spinner and label during form submission.
 *
 * Note: You need to manage `isSubmitting` state yourself in your form's submit handler.
 *
 * @example
 * import { useTransition } from 'react'
 *
 * const [isPending, startTransition] = useTransition()
 * const form = useFormStore({ defaultValues: { email: '' } })
 * form.useSubmit(async (state) => {
 *   startTransition(async () => {
 *     await submitForm(state.values)
 *   })
 * })
 * <Form store={form}>
 *   <FormInput name="email" type="email" />
 *   <FormSubmit submittingLabel="Submitting..." isSubmitting={isPending}>Submit</FormSubmit>
 * </Form>
 */
export const FormSubmit = styled(
  ({ children, className, submittingLabel, isSubmitting = false, ...rest }: FormSubmitProps) => {
    return (
      <AriaKitFormSubmit
        render={htmlProps => (
          <Button {...rest} {...htmlProps} className={className} type='submit'>
            <FormSubmitContent
              children={children}
              submittingLabel={submittingLabel}
              isSubmitting={isSubmitting}
            />
          </Button>
        )}
      />
    )
  }
)`
  position: relative;
`
